import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { ErrorFallback } from '~/components/ErrorFallback/ErrorFallback';
import { DOMAIN, REASON, reportError } from '~/services/telemetry';
import { isToleratedHydrationError } from '~/utils/hydrationErrors';
import { isStaleChunkError } from '~/utils/staleChunk';

interface BoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  /** Changing this clears the error, so a failed route is not a dead end. */
  resetKey: string;
}

class Boundary extends Component<BoundaryProps, { hasError: boolean }> {
  state = { hasError: false };

  // A hydration mismatch has to pass through untouched. React recovers from one
  // by re-rendering on the client, and the boundary at the root is built around
  // that; catching it here — closer to the route — would turn a page that fixes
  // itself into a visible failure.
  static getDerivedStateFromError(error: Error) {
    return { hasError: !isToleratedHydrationError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isToleratedHydrationError(error)) {
      return;
    }

    // A chunk that never arrived says nothing about the route's code, and the
    // two failures need opposite responses: one is a dropped connection to
    // count, the other is a bug to fix. Reported under the same tag they were
    // indistinguishable, and the chunk failures — which arrive in bursts,
    // because every navigation re-renders the same rejected import — buried
    // the render errors underneath them.
    const isChunkFailure = isStaleChunkError(error);

    reportError(isChunkFailure ? DOMAIN.chunk : DOMAIN.ui, error, {
      reason: isChunkFailure
        ? REASON.chunk.loadFailed
        : REASON.ui.routeRenderFailed,
      // A chunk lost to a deploy is routine, not a fault; reported as an
      // error it would move the error rate every release.
      level: isChunkFailure ? 'warning' : 'error',
      extras: { errorInfo },
    });
  }

  componentDidUpdate(previous: BoundaryProps) {
    if (this.state.hasError && previous.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/**
 * Keeps a route that fails from taking the whole app down with it.
 *
 * Without it the error reaches the boundary at the root, which renders nothing,
 * so one missing chunk blanks the page — header and footer included.
 *
 * A chunk that failed to arrive normally never gets this far: `lazyRoute`
 * reloads the page for it, onto the new build after a deploy or onto the same
 * one after a dropped connection. What reaches here has already spent that
 * reload, or arrived and then threw — so offering to try again by hand is the
 * honest response.
 *
 * Reloading is the only retry there is: React keeps a rejected `lazy` rejected,
 * and underneath it the browser keeps returning the same failure for a module
 * URL it once failed to fetch, without asking the network again.
 */
export const RouteErrorBoundary = ({ children }: { children: ReactNode }) => {
  const location = useLocation();

  return (
    <Boundary
      resetKey={location.pathname}
      fallback={<ErrorFallback className="min-h-[70vh] w-full" />}
    >
      {children}
    </Boundary>
  );
};
